import React from 'react';
import Skeleton from '@atlaskit/skeleton';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { JiraIssueCreateErrorBoundary } from '@jira/issue-create__issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import { SkeletonCompiled } from '@jira/platform__skeleton/src/styled.tsx';
import type RichTextEditViewWithEditorConfig from './RichTextEditViewWithEditorConfig.tsx';
import type { RichTextEditViewWithEditorConfigProps } from './RichTextEditViewWithEditorConfig.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AsyncRichTextEditViewWithEditorConfig = lazyAfterPaint<
	typeof RichTextEditViewWithEditorConfig
>(
	() =>
		import(
			/* webpackChunkName: "async-issue-create-description-editor-config" */ './RichTextEditViewWithEditorConfig.tsx'
		),
	{ ssr: false },
);

const RichTextEditViewWithLazyEditorConfigView = (
	props: RichTextEditViewWithEditorConfigProps,
): React.JSX.Element => {
	const { entryPointSkeletonHeight = '150px', shouldShowModernSkeleton = false } = props;

	// Must match AsyncRichTextEditView's own loading skeleton, which takes over once this chunk loads.
	const fallback = shouldShowModernSkeleton ? (
		<Skeleton height={entryPointSkeletonHeight} width="100%" isShimmering />
	) : (
		<SkeletonCompiled height={entryPointSkeletonHeight} />
	);

	return (
		<JiraIssueCreateErrorBoundary
			id="async.issue-create-description-editor-config"
			packageName="jiraIssueCreateModal"
			fallback="unmount"
		>
			<Placeholder name="issue-create-description-editor-config" fallback={fallback}>
				<AsyncRichTextEditViewWithEditorConfig {...props} />
			</Placeholder>
		</JiraIssueCreateErrorBoundary>
	);
};

export const RichTextEditViewWithLazyEditorConfig: typeof RichTextEditViewWithLazyEditorConfigView & {
	preload: () => void;
} = Object.assign(RichTextEditViewWithLazyEditorConfigView, {
	preload: () => {
		AsyncRichTextEditViewWithEditorConfig.preload();
	},
});
